'use client';

/**
 * DiagnosticsContext
 * - Fetches /api/diagnostics once on app init (after auth is ready)
 * - Caches the result in-memory (module-level singleton) to avoid repeated API calls
 * - Exposes the cached report + refresh function via React context
 * - Sends an in-app admin alert toast when any critical service fails
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DiagnosticResult {
  service: string;
  status: 'ok' | 'degraded' | 'error' | 'unconfigured';
  latency_ms: number;
  message: string;
  details?: Record<string, unknown>;
}

export interface DiagnosticsReport {
  timestamp: string;
  overall_status: 'healthy' | 'degraded' | 'critical';
  total_latency_ms: number;
  results: DiagnosticResult[];
}

interface DiagnosticsContextType {
  report: DiagnosticsReport | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: Date | null;
  /** Force a fresh fetch, bypassing the cache */
  refresh: () => Promise<void>;
  /** Critical services that are currently in error state */
  criticalFailures: DiagnosticResult[];
}

// ── In-memory module-level cache (survives re-renders, cleared on page reload) ─

interface CacheEntry {
  report: DiagnosticsReport;
  fetchedAt: Date;
}

let _cache: CacheEntry | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function isCacheValid(): boolean {
  if (!_cache) return false;
  return Date.now() - _cache.fetchedAt.getTime() < CACHE_TTL_MS;
}

// ── Critical services that trigger admin alerts ───────────────────────────────

const CRITICAL_SERVICE_PATTERNS = [
  'Database',
  'Storage (referti-pdf',
  'Edge Function: send-email',
  'Edge Function: firma-digitale',
];

function isCritical(service: string): boolean {
  return CRITICAL_SERVICE_PATTERNS.some((p) => service.includes(p));
}

// ── Context ───────────────────────────────────────────────────────────────────

const DiagnosticsContext = createContext<DiagnosticsContextType>({
  report: null,
  loading: false,
  error: null,
  lastFetchedAt: null,
  refresh: async () => {},
  criticalFailures: [],
});

export const useDiagnostics = () => useContext(DiagnosticsContext);

// ── Admin Alert Toast ─────────────────────────────────────────────────────────

interface AdminAlertToastProps {
  failures: DiagnosticResult[];
  onDismiss: () => void;
}

function AdminAlertToast({ failures, onDismiss }: AdminAlertToastProps) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 12000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div data-component-id="src/contexts/DiagnosticsContext.tsx:97:4" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="97" data-component-end-line="152" data-component-file="DiagnosticsContext.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-4%20right-4%20z-%5B9999%5D%20max-w-sm%20w-full%20bg-red-50%20border%20border-red-300%20rounded-xl%20shadow-lg%20p-4%20flex%20gap-3%20animate-in%20slide-in-from-bottom-4%22%7D"
      role="alert"
      aria-live="assertive"
      className="fixed bottom-4 right-4 z-[9999] max-w-sm w-full bg-red-50 border border-red-300 rounded-xl shadow-lg p-4 flex gap-3 animate-in slide-in-from-bottom-4"
    >
      <div data-component-id="src/contexts/DiagnosticsContext.tsx:102:6" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="102" data-component-end-line="116" data-component-file="DiagnosticsContext.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22shrink-0%20mt-0.5%22%7D" className="shrink-0 mt-0.5">
        <svg data-component-id="src/contexts/DiagnosticsContext.tsx:103:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="103" data-component-end-line="115" data-component-file="DiagnosticsContext.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%20text-red-600%22%7D"
          className="w-5 h-5 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path data-component-id="src/contexts/DiagnosticsContext.tsx:110:10" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="110" data-component-end-line="114" data-component-file="DiagnosticsContext.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      </div>
      <div data-component-id="src/contexts/DiagnosticsContext.tsx:117:6" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="117" data-component-end-line="136" data-component-file="DiagnosticsContext.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
        <p data-component-id="src/contexts/DiagnosticsContext.tsx:118:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="118" data-component-end-line="122" data-component-file="DiagnosticsContext.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-semibold%20text-red-800%22%7D" className="text-sm font-semibold text-red-800">
          {failures.length === 1
            ? 'Servizio critico in errore'
            : `${failures.length} servizi critici in errore`}
        </p>
        <ul data-component-id="src/contexts/DiagnosticsContext.tsx:123:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="123" data-component-end-line="129" data-component-file="DiagnosticsContext.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22mt-1%20space-y-0.5%22%7D" className="mt-1 space-y-0.5">
          {failures.map((f) => (
            <li data-component-id="src/contexts/DiagnosticsContext.tsx:125:12" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="125" data-component-end-line="127" data-component-file="DiagnosticsContext.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22text-xs%20text-red-700%20truncate%22%2C%22textContent%22%3A%22%E2%80%A2%22%7D" key={f.service} className="text-xs text-red-700 truncate">
              • {f.service}
            </li>
          ))}
        </ul>
        <a data-component-id="src/contexts/DiagnosticsContext.tsx:130:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="130" data-component-end-line="135" data-component-file="DiagnosticsContext.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22%2Fhealth-check%22%2C%22className%22%3A%22mt-2%20inline-block%20text-xs%20font-medium%20text-red-700%20underline%20hover%3Atext-red-900%22%2C%22textContent%22%3A%22Vai%20alla%20Health%20Check%20%E2%86%92%22%7D"
          href="/health-check"
          className="mt-2 inline-block text-xs font-medium text-red-700 underline hover:text-red-900"
        >
          Vai alla Health Check →
        </a>
      </div>
      <button data-component-id="src/contexts/DiagnosticsContext.tsx:137:6" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="137" data-component-end-line="151" data-component-file="DiagnosticsContext.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22shrink-0%20text-red-400%20hover%3Atext-red-600%20transition-colors%22%7D"
        onClick={onDismiss}
        aria-label="Chiudi avviso"
        className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
      >
        <svg data-component-id="src/contexts/DiagnosticsContext.tsx:142:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="142" data-component-end-line="150" data-component-file="DiagnosticsContext.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-4%20h-4%22%7D"
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path data-component-id="src/contexts/DiagnosticsContext.tsx:149:10" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="149" data-component-end-line="149" data-component-file="DiagnosticsContext.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function DiagnosticsProvider({ children }: { children: React.ReactNode }) {
  const { isAdminLab, loading: authLoading } = useAuth();
  const [report, setReport] = useState<DiagnosticsReport | null>(_cache?.report ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(_cache?.fetchedAt ?? null);
  const [showAlert, setShowAlert] = useState(false);
  const alertShownRef = useRef(false);

  const fetchDiagnostics = useCallback(
    async (force = false) => {
      // Use cache if valid and not forced
      if (!force && isCacheValid() && _cache) {
        setReport(_cache.report);
        setLastFetchedAt(_cache.fetchedAt);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await fetch('/api/diagnostics', { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: DiagnosticsReport = await res.json();

        // Update module-level cache
        _cache = { report: data, fetchedAt: new Date() };

        setReport(data);
        setLastFetchedAt(_cache.fetchedAt);

        // Trigger admin alert for critical failures (only once per session)
        if (isAdminLab && !alertShownRef.current) {
          const failures = data.results.filter(
            (r) => r.status === 'error' && isCritical(r.service)
          );
          if (failures.length > 0) {
            alertShownRef.current = true;
            setShowAlert(true);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore nel recupero diagnostica');
      } finally {
        setLoading(false);
      }
    },
    [isAdminLab]
  );

  // Fetch once on mount after auth is resolved
  useEffect(() => {
    if (!authLoading) {
      fetchDiagnostics();
    }
  }, [authLoading, fetchDiagnostics]);

  const refresh = useCallback(async () => {
    alertShownRef.current = false;
    await fetchDiagnostics(true);
  }, [fetchDiagnostics]);

  const criticalFailures = (report?.results ?? []).filter(
    (r) => r.status === 'error' && isCritical(r.service)
  );

  return (
    <DiagnosticsContext.Provider data-component-id="src/contexts/DiagnosticsContext.tsx:226:4" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="226" data-component-end-line="233" data-component-file="DiagnosticsContext.tsx" data-component-name="DiagnosticsContext.Provider" data-component-content="%7B%22elementName%22%3A%22DiagnosticsContext.Provider%22%7D"
      value={{ report, loading, error, lastFetchedAt, refresh, criticalFailures }}
    >
      {children}
      {showAlert && isAdminLab && criticalFailures.length > 0 && (
        <AdminAlertToast data-component-id="src/contexts/DiagnosticsContext.tsx:231:8" data-component-path="src/contexts/DiagnosticsContext.tsx" data-component-line="231" data-component-end-line="231" data-component-file="DiagnosticsContext.tsx" data-component-name="AdminAlertToast" data-component-content="%7B%22elementName%22%3A%22AdminAlertToast%22%7D" failures={criticalFailures} onDismiss={() => setShowAlert(false)} />
      )}
    </DiagnosticsContext.Provider>
  );
}
